import { useAuth } from "@src/contexts/AuthProvider";
import { ApiCallParams, sudoziFetchBase } from "@src/infrastructure/apiCall";
import {
  MutationRoute,
  PublicMutationRoute,
} from "@src/sudozi-schemas/api-route-schemas";
import { useMutation, UseMutationOptions } from "react-query";
import { z } from "zod";

export function useSudoziMutation<
  R extends MutationRoute,
  P extends R["params"],
  O extends R["output"],
>({
  route,
  mutationKey,
  options,
  params,
}: P extends z.ZodVoid
  ? {
      route: R;
      mutationKey: Parameters<typeof useMutation>[0];
      options?: Omit<
        UseMutationOptions<
          z.infer<O>,
          unknown,
          Omit<ApiCallParams<R>, "route" | "params"> & { params?: z.infer<P> }
        >,
        "mutationKey" | "mutationFn"
      >;
      params?: z.infer<P>;
    }
  : {
      route: R;
      mutationKey: Parameters<typeof useMutation>[0];
      options?: Omit<
        UseMutationOptions<
          z.infer<O>,
          unknown,
          Omit<ApiCallParams<R>, "route" | "params"> & { params?: z.infer<P> }
        >,
        "mutationKey" | "mutationFn"
      >;
      params: z.infer<P>;
    }) {
  const { tokenUserData } = useAuth();
  const outputSchema = route.output as O;

  type ApiArgs = Omit<ApiCallParams<R>, "route" | "params"> & {
    params?: z.infer<P>;
  };

  return useMutation<z.infer<O>, unknown, ApiArgs>(
    mutationKey,
    async ({ body, params: paramsOverride }: ApiArgs) => {
      if (!tokenUserData) {
        console.error("No valid company token found to make request");
        return null;
      }
      const result = await sudoziFetchBase({
        route,
        body,
        params: paramsOverride ?? params,
      });

      if (outputSchema && !(outputSchema instanceof z.ZodVoid)) {
        return outputSchema.parse(await result.json());
      }
    },
    options,
  );
}

export function useSudoziPublicMutation<
  R extends PublicMutationRoute,
  P extends R["params"],
  O extends R["output"],
>({
  route,
  mutationKey,
  options,
  params,
}: P extends z.ZodVoid
  ? {
      route: R;
      mutationKey: Parameters<typeof useMutation>[0];
      options?: Parameters<typeof useMutation>[2];
      params?: z.infer<P>;
    }
  : {
      route: R;
      mutationKey: Parameters<typeof useMutation>[0];
      options?: Parameters<typeof useMutation>[2];
      params: z.infer<P>;
    }) {
  const outputSchema = route.output as O;

  type ApiArgs = Omit<ApiCallParams<R>, "route" | "params"> & {
    params?: z.infer<P>;
  };

  return useMutation<z.infer<O>, unknown, ApiArgs>(
    mutationKey,
    async ({ body, params: paramsOverride }: ApiArgs) => {
      const result = await sudoziFetchBase({
        route,
        body,
        params: paramsOverride ?? params,
      });

      if (outputSchema && !(outputSchema instanceof z.ZodVoid)) {
        return outputSchema.parse(await result.json());
      }
    },

    options,
  );
}

export function useSudoziVendorMutation<
  R extends MutationRoute,
  P extends R["params"],
  O extends R["output"],
>({
  route,
  mutationKey,
  options,
  params,
}: P extends z.ZodVoid
  ? {
      route: R;
      mutationKey: Parameters<typeof useMutation>[0];
      options?: Parameters<typeof useMutation>[2];
      params?: z.infer<P>;
    }
  : {
      route: R;
      mutationKey: Parameters<typeof useMutation>[0];
      options?: Parameters<typeof useMutation>[2];
      params: z.infer<P>;
    }) {
  const { vendorUser } = useAuth();
  const outputSchema = route.output as O;

  type ApiArgs = Omit<ApiCallParams<R>, "route" | "params"> & {
    params?: z.infer<P>;
  };

  return useMutation<z.infer<O>, unknown, ApiArgs>(
    mutationKey,
    async ({ body, params: paramsOverride }: ApiArgs) => {
      if (!vendorUser) {
        console.error("No valid vendor portal token found to make request");
        return null;
      }
      const result = await sudoziFetchBase({
        route,
        body,
        params: paramsOverride ?? params,
      });

      if (outputSchema && !(outputSchema instanceof z.ZodVoid)) {
        return outputSchema.parse(await result.json());
      }
    },

    options,
  );
}
